import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Menu, Transition } from "@headlessui/react";
import { Button } from "../../ui/button";
import { Calendar } from "lucide-react";
import Input from "../../Tags/Input";
import { useFormik } from "formik";
import * as yup from "yup";
import { useUsageChart } from "../../../apiHooks/useUsageChart";
import { useStoreUsageBilling } from "../../../stores/useStoreUsageBilling";
import { ResponsiveBar } from "@nivo/bar";
import { Skeleton } from "../../ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import Image from "../../Tags/Image";
import { useCaptureEvent } from "../../../hooks/useCaptureEvent";

const CHART_COLOR_PALETTE = [
  "#003F5C",
  // "#2F4B7C",
  "#665191",
  "#A05195",
  "#D45087",
  // "#F95D6A",
  // "#FF7C43",
  // "#FFA600",
];

const getSeriesIdentifiers = (item, index) => {
  const displayKey = item?.product_display_name || item?.product_name || `Series ${index + 1}`;
  const idKey = item?.product_name || item?.product || displayKey;
  return { displayKey, idKey };
};

export default function UsageCharts({ activeWorkspace }) {
  const [selectedFilters, setSelectedFilters] = useState({
    period: undefined,
    data_type: undefined,
    product: undefined,
    start_date: undefined,
    end_date: undefined,
  });
  const { isLoading, isFetching } = useUsageChart(activeWorkspace?.id, selectedFilters);
  const { usageChartResponse } = useStoreUsageBilling();
  const {
    total_credits_used: totalCreditsUsed,
    series: seriesData,
    metadata: metaData,
    filters,
  } = usageChartResponse || {};

  const buttonRef = useRef();
  const [activeDateFilter, setActiveDateFilter] = useState({});
  const [customDates, setCustomDates] = useState({ from: null, to: null });
  const captureEvent = useCaptureEvent();

  // analytics page data
  const dateStaticFilters = [
    {
      name: "7 days",
      value: "7d",
    },
    {
      name: "30 days",
      value: "30d",
    },
    {
      name: "90 days",
      value: "90d",
    },
    {
      name: "1 year",
      value: "1y",
    },
  ];

  const validationSchema = yup.object().shape({
    from_date: yup.string().required("From date is required"),
    to_date: yup.string().required("To date is required"),
  });
  const formik = useFormik({
    initialValues: {
      from_date: "",
      to_date: "",
    },
    validationSchema,
    onSubmit: (values) => {
      updateSelectedFilters((prev) => ({
        ...prev,
        period: "custom",
        start_date: values.from_date,
        end_date: values.to_date,
      }));
      setCustomDates({
        from: values.from_date,
        to: values.to_date,
      });
      formik.setSubmitting(false);
      setActiveDateFilter({
        name: "Custom Range",
        value: "custom",
      });
      captureEvent("BP_CustomRangeApply", {
        from: values.from_date,
        to: values.to_date,
      });
    },
  });
  const periodFilters = useMemo(() => {
    if (Array.isArray(filters?.period) && filters.period.length > 0) {
      return filters.period.map((item) => ({
        name: item?.label || item?.name || item?.value,
        value: item?.value,
      }));
    }
    return dateStaticFilters;
  }, [filters?.period]);

  const dataTypeFilters = useMemo(() => {
    if (Array.isArray(filters?.data_type) && filters.data_type.length > 0) {
      return filters.data_type.map((item) => ({
        name: item?.label || item?.value,
        value: item?.value,
      }));
    }
    return [];
  }, [filters?.data_type]);

  const updateSelectedFilters = (updater) => {
    setSelectedFilters((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      return next;
    });
  };

  useEffect(() => {
    if (!metaData || isFetching) {
      return;
    }

    if (metaData?.period?.type) {
      const { type, start_date: startDate, end_date: endDate } = metaData.period;

      if (type === "custom" && startDate && endDate) {
        const formattedStart = startDate?.slice(0, 10);
        const formattedEnd = endDate?.slice(0, 10);
        setCustomDates({
          from: formattedStart,
          to: formattedEnd,
        });

        updateSelectedFilters((prev) => {
          if (prev.period === type && prev.start_date === formattedStart && prev.end_date === formattedEnd) {
            return prev;
          }
          return {
            ...prev,
            period: type,
            start_date: formattedStart,
            end_date: formattedEnd,
          };
        });
      } else {
        setCustomDates({ from: null, to: null });
        updateSelectedFilters((prev) => {
          if (prev.period === type && !prev.start_date && !prev.end_date) {
            return prev;
          }
          return {
            ...prev,
            period: type,
            start_date: undefined,
            end_date: undefined,
          };
        });
      }
    }

    if (metaData?.data_type) {
      updateSelectedFilters((prev) => {
        if (prev.data_type === metaData.data_type) {
          return prev;
        }
        return {
          ...prev,
          data_type: metaData.data_type,
        };
      });
    }
  }, [metaData, isFetching]);

  useEffect(() => {
    if (Array.isArray(filters?.data_type) && filters.data_type.length > 0) {
      const available = filters.data_type.map((item) => item.value);
      updateSelectedFilters((prev) => {
        if (prev.data_type && available.includes(prev.data_type)) {
          return prev;
        }
        const fallback =
          metaData?.data_type && available.includes(metaData.data_type) ? metaData.data_type : available[0];
        if (!fallback) return prev;
        return {
          ...prev,
          data_type: fallback,
        };
      });
    }
  }, [filters?.data_type, metaData?.data_type]);

  useEffect(() => {
    if (Array.isArray(filters?.product) && filters.product.length > 0) {
      const available = filters.product.map((item) => item.value);
      updateSelectedFilters((prev) => {
        if (!prev.product) {
          return prev;
        }
        if (available.includes(prev.product)) {
          return prev;
        }
        return {
          ...prev,
          product: undefined,
        };
      });
    }
  }, [filters?.product]);

  useEffect(() => {
    if (!selectedFilters.period) {
      return;
    }
    if (selectedFilters.period !== "custom") {
      setCustomDates({ from: null, to: null });
    }

    const matchingFilter = periodFilters.find((item) => item.value === selectedFilters.period);

    if (matchingFilter) {
      if (activeDateFilter?.value !== matchingFilter.value) {
        setActiveDateFilter(matchingFilter);
      }
    } else if (selectedFilters.period === "custom") {
      if (activeDateFilter?.value !== "custom") {
        setActiveDateFilter({ name: "Custom Range", value: "custom" });
      }
    }
  }, [selectedFilters.period, periodFilters]);

  const chartKeys = useMemo(() => {
    if (!Array.isArray(seriesData)) {
      return [];
    }
    return seriesData
      .map((item, index) => {
        const { displayKey } = getSeriesIdentifiers(item, index);
        return displayKey;
      })
      .filter(Boolean);
  }, [seriesData]);

  const productColorAssignments = useMemo(() => {
    const assignments = {};
    let paletteIndex = 0;

    const assignColor = (identifier) => {
      if (!identifier) {
        return undefined;
      }
      if (!assignments[identifier]) {
        assignments[identifier] = CHART_COLOR_PALETTE[paletteIndex % CHART_COLOR_PALETTE.length];
        paletteIndex += 1;
      }
      return assignments[identifier];
    };

    if (Array.isArray(filters?.product)) {
      filters.product.forEach((item) => {
        const color = assignColor(item?.value || item?.label || item?.name);
        [item?.label, item?.name].forEach((alias) => {
          if (alias) {
            assignments[alias] = color;
          }
        });
      });
    }

    if (Array.isArray(seriesData)) {
      seriesData.forEach((item, index) => {
        const { idKey, displayKey } = getSeriesIdentifiers(item, index);
        const color = assignColor(idKey);
        if (displayKey && color) {
          assignments[displayKey] = color;
        }
      });
    }

    return assignments;
  }, [filters?.product, seriesData]);

  const colorMap = useMemo(() => {
    if (!Array.isArray(seriesData)) {
      return {};
    }
    return seriesData.reduce((acc, item, index) => {
      const { displayKey, idKey } = getSeriesIdentifiers(item, index);
      if (displayKey) {
        acc[displayKey] =
          productColorAssignments[idKey] ||
          productColorAssignments[displayKey] ||
          CHART_COLOR_PALETTE[index % CHART_COLOR_PALETTE.length];
      }
      return acc;
    }, {});
  }, [seriesData, productColorAssignments]);

  const chartData = useMemo(() => {
    if (!Array.isArray(seriesData) || seriesData.length === 0) {
      return [];
    }

    const aggregated = new Map();

    const resolvePointValue = (rawValue) => {
      if (rawValue === null || rawValue === undefined) {
        return 0;
      }
      if (typeof rawValue === "number") {
        return rawValue;
      }
      if (typeof rawValue === "object") {
        if (typeof rawValue.total === "number") {
          return rawValue.total;
        }
        if (rawValue.total !== undefined) {
          const parsedInnerTotal = Number(rawValue.total);
          if (Number.isFinite(parsedInnerTotal)) {
            return parsedInnerTotal;
          }
        }
        if (rawValue.total && typeof rawValue.total === "object") {
          if (typeof rawValue.total.total === "number") {
            return rawValue.total.total;
          }
          const nestedTotal = Number(rawValue.total.total);
          if (Number.isFinite(nestedTotal)) {
            return nestedTotal;
          }
        }
      }
      const parsed = Number(rawValue);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    seriesData.forEach((series, seriesIndex) => {
      const seriesKey = series?.product_display_name || series?.product_name || `Series ${seriesIndex + 1}`;

      series?.data?.forEach((point, pointIndex) => {
        const label = point?.label || `Label ${pointIndex + 1}`;
        const numericValue = resolvePointValue(point?.value);

        if (!aggregated.has(label)) {
          aggregated.set(label, {
            label,
            _sortValue:
              metaData?.data_type === "date" ? point?.date || point?.label || pointIndex : aggregated.size,
          });
        }

        const entry = aggregated.get(label);
        entry[seriesKey] = numericValue;

        if (metaData?.data_type === "date" && point?.date) {
          entry._sortValue = point.date;
        }
      });
    });

    const entries = Array.from(aggregated.values());

    if (metaData?.data_type === "date") {
      entries.sort((a, b) => new Date(a._sortValue) - new Date(b._sortValue));
    }

    return entries.map(({ _sortValue, ...rest }) => rest);
  }, [seriesData, metaData?.data_type]);

  const shouldRotateTicks = useMemo(() => {
    if (!chartData.length) {
      return false;
    }

    if (metaData?.data_type === "date") {
      return chartData.length > 10;
    }

    return chartData.length > 5;
  }, [chartData, metaData?.data_type]);

  const hasChartData = chartData.length > 0 && chartKeys.length > 0;

  const maxValue = useMemo(() => {
    if (!hasChartData) {
      return undefined;
    }
    let max = 0;
    chartData.forEach((entry) => {
      chartKeys.forEach((key) => {
        const numeric = Number(entry[key]);
        if (Number.isFinite(numeric) && numeric > max) {
          max = numeric;
        }
      });
    });
    return max;
  }, [chartData, chartKeys, hasChartData]);

  const roundedMax = useMemo(() => {
    const candidates = [Number(totalCreditsUsed), maxValue].filter(
      (value) => Number.isFinite(value) && value > 0
    );
    if (candidates.length === 0) {
      return undefined;
    }

    const target = Math.max(...candidates);

    if (target <= 50) {
      return Math.ceil(target / 10) * 10;
    }

    if (target <= 200) {
      return Math.ceil(target / 25) * 25;
    }

    if (target <= 1000) {
      return Math.ceil(target / 100) * 100;
    }

    return Math.ceil(target / 250) * 250;
  }, [maxValue, totalCreditsUsed]);

  const renderChart = () => {
    if (isLoading || isFetching) {
      return (
        <div className="flex items-center justify-center h-[320px]">
          <Skeleton className="h-full w-full" />
        </div>
      );
    }

    if (!hasChartData) {
      return (
        <div className="text-center mb-6">
          <div className="inline-flex justify-center flex-col items-center">
            <Image src="/images/icons/untitledui/no-data-usage.svg" className="max-w-[200px] no-invert" />
            <p className="text-gray-600 text-lg">We couldn’t find anything here</p>
          </div>
        </div>
      );
    }

    return (
      <div className="h-[360px]">
        <ResponsiveBar
          data={chartData}
          keys={chartKeys}
          indexBy="label"
          margin={{ top: 40, right: 32, bottom: shouldRotateTicks ? 110 : 70, left: 70 }}
          padding={0.3}
          innerPadding={2}
          valueScale={{ type: "linear", max: roundedMax }}
          indexScale={{ type: "band", round: true }}
          colors={(bar) => colorMap[bar.id] || CHART_COLOR_PALETTE[0]}
          borderRadius={4}
          enableLabel={false}
          enableGridY={true}
          axisTop={null}
          axisRight={null}
          axisLeft={{
            tickSize: 0,
            tickPadding: 12,
            legend:
              roundedMax !== undefined
                ? `Credits used`
                : maxValue !== undefined
                  ? `Credits used`
                  : "Credits used",
            legendOffset: -60,
          }}
          axisBottom={{
            tickSize: 0,
            tickPadding: 14,
            tickRotation: shouldRotateTicks ? -40 : 0,
          }}
          tooltip={({ id, value, indexValue, color }) => (
            <div className="rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm">
              <div className="font-medium" style={{ color }}>
                {id}
              </div>
              <div className="text-muted-foreground">{indexValue}</div>
              <div className="mt-1 font-semibold">{value ?? 0}</div>
            </div>
          )}
          theme={{
            axis: {
              ticks: {
                text: {
                  fill: "#475467",
                  fontSize: 12,
                },
              },
            },
            grid: {
              line: {
                stroke: "#706f6f0d",
                strokeWidth: 1,
              },
            },
            tooltip: {
              container: {
                background: "#fff",
                color: "#111827",
                fontSize: 12,
              },
            },
          }}
          legends={
            chartKeys.length > 1
              ? [
                  {
                    dataFrom: "keys",
                    anchor: "bottom",
                    direction: "row",
                    justify: false,
                    translateY: 60,
                    itemsSpacing: 12,
                    itemWidth: 120,
                    itemHeight: 16,
                    itemDirection: "left-to-right",
                    itemOpacity: 1,
                    symbolSize: 12,
                    symbolShape: "circle",
                  },
                ]
              : []
          }
          role="application"
          ariaLabel="Usage credits chart"
        />
      </div>
    );
  };

  return (
    <div>
      <div className="flex space-x-2 items-center">
        <div className="inline-flex rounded-md border bg-background overflow-hidden">
          {periodFilters.map((item, index) => (
            <span
              key={item.value}
              onClick={() => {
                setCustomDates({ from: null, to: null });
                setActiveDateFilter(item);
                updateSelectedFilters((prev) => {
                  const noCustomDates = !prev.start_date && !prev.end_date;
                  if (prev.period === item.value && noCustomDates) {
                    return prev;
                  }
                  return {
                    ...prev,
                    period: item.value,
                    start_date: undefined,
                    end_date: undefined,
                  };
                });
                formik.resetForm();
                captureEvent("BP_PeriodFilter", {
                  period: item.value,
                });
              }}
              className={`${item?.value === "custom" && "hidden"} py-3 font-bold cursor-pointer px-4 ${index + 1 < periodFilters.length ? "border-r" : ""} ${activeDateFilter?.value === item?.value ? "bg-[#E9EAEB] dark:bg-gray-100" : ""}`}
            >
              {item?.name}
            </span>
          ))}
        </div>
        <Menu as="div" className="relative inline-block text-left">
          <div>
            <Menu.Button ref={buttonRef}>
              <Button
                variant="outline"
                className={`h-[47px] border border-border ${customDates?.from && customDates?.to && activeDateFilter && activeDateFilter?.value === "custom" && "bg-[#E9EAEB]  dark:bg-gray-100 "}`}
              >
                <Calendar className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
                Custom Range{" "}
                {customDates?.from &&
                  customDates?.to &&
                  activeDateFilter &&
                  activeDateFilter?.value === "custom" && (
                    <>
                      : {customDates?.from} - {customDates?.to}
                    </>
                  )}
              </Button>
            </Menu.Button>
          </div>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items
              className="absolute left-0 z-10 mt-1 w-full min-w-[250px] origin-top-right 
                          divide-y divide-border rounded-md bg-background shadow-lg 
                          ring-1 ring-border ring-opacity-5 focus:outline-none"
            >
              <div className="p-4">
                <form onSubmit={formik.handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="from_date" className="block text-sm font-medium text-paragraph  mb-1">
                      From:
                    </label>
                    <Input
                      id="from_date"
                      name="from_date"
                      type="date"
                      min="2022-01-01"
                      max={new Date().toISOString().split("T")[0]}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.from_date}
                    />
                    {formik.touched.from_date && formik.errors.from_date && (
                      <p className="mt-1 text-red-500 text-sm">{formik.errors.from_date}</p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="to_date" className="block text-sm font-medium text-paragraph mb-1">
                      To:
                    </label>
                    <Input
                      id="to_date"
                      name="to_date"
                      type="date"
                      min="2022-01-01"
                      max={new Date().toISOString().split("T")[0]}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.to_date}
                    />
                    {formik.touched.to_date && formik.errors.to_date && (
                      <p className="mt-1 text-red-500 text-sm">{formik.errors.to_date}</p>
                    )}
                  </div>
                  <div className="mt-6">
                    <Button
                      type="submit"
                      disabled={
                        formik.isSubmitting ||
                        !formik.isValid ||
                        (!formik.values.from_date && !formik.values.to_date)
                      }
                      onClick={() => {
                        buttonRef.current.click();
                      }}
                    >
                      Done
                    </Button>
                  </div>
                </form>
              </div>
            </Menu.Items>
          </Transition>
        </Menu>

        {Array.isArray(filters?.product) && filters.product.length > 0 && (
          <Select
            value={selectedFilters.product ?? "__all_products__"}
            onValueChange={(value) =>
              updateSelectedFilters((prev) => {
                const nextValue = value === "__all_products__" ? undefined : value;
                if (prev.product === nextValue) {
                  return prev;
                }
                captureEvent("BP_ProductFilter", {
                  product: nextValue || "all",
                });
                return {
                  ...prev,
                  product: nextValue,
                };
              })
            }
          >
            <SelectTrigger className="w-[200px] font-bold text-left h-[47px] shadow-none bg-background border border-border">
              <div className="flex space-x-2 items-center">
                <Image src="/images/icons/untitledui/filter.svg" width="16" height="18" alt="Filter" />
                <SelectValue placeholder="Product" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all_products__">All products</SelectItem>
              {filters.product.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label || item.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      <div className="leading-5 border-border border  bg-background py-4 px-6 rounded-xl mt-6">
        <p className="mb-1.5">Total Credits Used</p>
        <p className="text-2xl font-bold">{totalCreditsUsed || "-"}</p>
      </div>
      <div id="usage_billing_chart" className="mt-6 border border-border bg-background px-6 py-4 rounded-xl">
        {dataTypeFilters.length > 0 && (
          <div className="flex items-center justify-end mb-4">
            <div className="inline-flex rounded-md border bg-background overflow-hidden">
              {dataTypeFilters.map((item, index) => (
                <span
                  key={item.value}
                  onClick={() => {
                    updateSelectedFilters((prev) => {
                      if (prev.data_type === item.value) {
                        return prev;
                      }
                      captureEvent("BP_DataTypeFilter", {
                        data_type: item.value,
                      });
                      return {
                        ...prev,
                        data_type: item.value,
                      };
                    });
                  }}
                  className={`py-2 px-4 text-sm font-medium cursor-pointer ${index + 1 < dataTypeFilters.length ? "border-r" : ""} ${
                    selectedFilters.data_type === item.value ? "bg-[#E9EAEB] dark:bg-gray-100" : ""
                  }`}
                >
                  {item.name}
                </span>
              ))}
            </div>
          </div>
        )}
        {renderChart()}
      </div>
    </div>
  );
}
